import {
  addDevice, // Import addDevice
  fetchDevices, // Import fetchDevices
  logout,
  mfaSignIn,
  removeDevice, // Import removeDevice
  resetSkipped as resetSkippedFromLogin,
  setSkipped as setSkippedFromLogin,
  verifyOtp
} from '@/features/login/slice';
import { sportsAuthorityService } from '@/services';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

const initialState = {
  devices: [],
  error: null,
  flowId: null,
  hasFetchedDevices: false,
  isFetchDevicesLoading: false,
  isFlowIdLoading: false,
  isMfaSignInLoading: false,
  isVerifyOtpLoading: false,
  skipped: false,
  status: 'INIT',
  verifyStatus: null,
  otpRequested: false, // New state item
  hasRecentlyAddedDevice: false // New state item
};

const EXPIRATION_TIME_MS = 30 * 60 * 1000; // 30 minutes expiration time

export const fetchFlowId = createAsyncThunk('mfa/fetchFlowId', async (_, { getState }) => {
  const state = getState();
  const user = state.auth.user;
  const response = await sportsAuthorityService.authorizeMfa(user);
  return response.data;
});

export const fetchFlowStatus = createAsyncThunk('mfa/fetchFlowStatus', async ({ flowId }) => {
  const response = await sportsAuthorityService.fetchFlowStatus(flowId);
  return response.data;
});

const mfaSlice = createSlice({
  name: 'mfa',
  initialState,
  reducers: {
    setSkipped: (state) => {
      state.skipped = true;
    },
    resetSkipped: (state) => {
      state.skipped = false;
    },
    resetOtpRequested: (state) => {
      state.otpRequested = false; // Reset otpRequested
    },
    setHasRecentlyAddedDevice: (state, action) => {
      state.hasRecentlyAddedDevice = action.payload; // Update hasRecentlyAddedDevice
      if (action.payload) {
        const expiration = Date.now() + EXPIRATION_TIME_MS;
        sessionStorage.setItem(
          'hasRecentlyAddedDevice',
          JSON.stringify({ value: true, expiration }) // Store value with expiration
        );
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(setSkippedFromLogin, (state) => {
        state.skipped = true;
      })
      .addCase(resetSkippedFromLogin, (state) => {
        state.skipped = false;
      })
      .addCase(logout.fulfilled, (state) => {
        Object.assign(state, initialState); // Reset state on logout
      })
      .addCase(fetchFlowId.fulfilled, (state, action) => {
        state.flowId = action.payload.flowId;
        state.status = action.payload.mfaStatus;
        state.verifyStatus = action.payload.verifyStatus;
        state.devices = action.payload.devices;
        state.isFlowIdLoading = false;
      })
      .addCase(fetchFlowId.rejected, (state, action) => {
        state.error = action.error.message;
        state.isFlowIdLoading = false;
      })
      .addCase(fetchFlowId.pending, (state) => {
        state.isFlowIdLoading = true;
      })
      .addCase(mfaSignIn.fulfilled, (state, action) => {
        state.otpRequested = true; // Set otpRequested to true when OTP is requested
        state.isMfaSignInLoading = false;
        state.flowId = action.payload.flowId;
        state.status = action.payload.mfaStatus;
      })
      .addCase(mfaSignIn.rejected, (state, action) => {
        state.error = action.error.message;
        state.isMfaSignInLoading = false;
      })
      .addCase(mfaSignIn.pending, (state) => {
        state.otpRequested = false; // Reset otpRequested when a new request starts
        state.isMfaSignInLoading = true;
      })
      .addCase(fetchDevices.pending, (state) => {
        state.isFetchDevicesLoading = true; // Set loading state
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        state.devices = action.payload; // Update devices
        state.hasFetchedDevices = true; // Update hasFetchedDevicesn
        state.isFetchDevicesLoading = false; // Reset loading state
      })
      .addCase(fetchDevices.rejected, (state) => {
        state.hasFetchedDevices = false; // Reset hasFetchedDevices on failure
        state.isFetchDevicesLoading = false; // Reset loading state
      })
      .addCase(removeDevice.fulfilled, (state) => {
        Object.assign(state, initialState); // Reset state on device removal
        state.hasRecentlyAddedDevice = false; // Set hasRecentlyAddedDevice to false
        sessionStorage.setItem(
          'hasRecentlyAddedDevice',
          JSON.stringify({ value: false }) // Update session storage
        );
      })
      .addCase(addDevice.fulfilled, (state) => {
        state.hasRecentlyAddedDevice = true; // Set hasRecentlyAddedDevice to true
        const expiration = Date.now() + EXPIRATION_TIME_MS;
        sessionStorage.setItem(
          'hasRecentlyAddedDevice',
          JSON.stringify({ value: true, expiration }) // Store value with expiration
        );
      })
      .addCase(fetchFlowStatus.fulfilled, (state, action) => {
        state.status = action.payload.mfaStatus; // Update status
        state.verifyStatus = action.payload.verifyStatus; // Update verifyStatus
        state.error = action.payload.error || null; // Update error
        state.isFlowIdLoading = false; // Reset loading state
        state.devices = action.payload.devices; // Update devices
      })
      .addCase(fetchFlowStatus.rejected, (state, action) => {
        state.error = action.error.message; // Set error message
        state.isFlowIdLoading = false; // Reset loading state
      })
      .addCase(fetchFlowStatus.pending, (state) => {
        state.isFlowIdLoading = true; // Set loading state
      })
      .addCase(verifyOtp.pending, (state) => {
        state.otpRequested = true; // Set loading state
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.otpRequested = true; // Reset loading state
        state.status = action.payload.status;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.error = action.error.message; // Set error message
        state.otpRequested = true; // Reset loading state
      });
  }
});

export const { setSkipped, resetOtpRequested, resetSkipped, setHasRecentlyAddedDevice } =
  mfaSlice.actions;
export default mfaSlice.reducer;
